// Pure helpers for Scan schedules: plain wording, grouping by day, and the request a schedule is saved with.
import { scanLabel } from './webScanLogic';
import { kindLabel } from './networkScanLogic';
import { STARTERS, buildRuns, canRun, toPayload } from './templateLogic';

export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const FREQUENCIES = [{ id: 'hourly', label: 'hour' }, { id: 'daily', label: 'day' }, { id: 'weekly', label: 'week' }];

export const whatLabel = (type) => (String(type).startsWith('network_') ? kindLabel(type) : scanLabel(type));

export function everyText(s) {
  const t = String(s.run_at || '00:00:00').slice(0, 5);
  if (s.frequency === 'hourly') return `Every hour at :${t.slice(3)}`;
  if (s.frequency === 'weekly') return `Every ${DAYS[Number(s.day_of_week) || 0]} at ${t}`;
  return `Every day at ${t}`;
}

export const lastText = (s, ago) => {
  if (s.last_status === 'failed') return { text: `Last run failed${s.last_error ? `: ${s.last_error.slice(0, 50)}` : ''}`, bad: true };
  if (s.last_run_at) return { text: `Last run started ${ago(s.last_run_at)}`, bad: false };
  return { text: 'Never ran', bad: false };
};

const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

// Today / Tomorrow / Later, soonest first; paused schedules go last.
export function groupSchedules(list, now = new Date()) {
  const today = dayStart(now);
  const groups = { Today: [], Tomorrow: [], Later: [], Paused: [] };
  [...list].sort((a, b) => new Date(a.next_run_at || 0) - new Date(b.next_run_at || 0)).forEach((s) => {
    if (!s.enabled || !s.next_run_at) return groups.Paused.push(s);
    const diff = Math.round((dayStart(new Date(s.next_run_at)) - today) / 86400000);
    (diff <= 0 ? groups.Today : diff === 1 ? groups.Tomorrow : groups.Later).push(s);
  });
  return Object.entries(groups).filter(([, v]) => v.length).map(([day, items]) => ({ day, items }));
}

export const counts = (list) => ({
  total: list.length, active: list.filter((s) => s.enabled).length,
  paused: list.filter((s) => !s.enabled).length, failed: list.filter((s) => s.last_status === 'failed').length
});

// What the sentence builder offers: ready-made setups first, then runnable saved templates.
export const setupChoices = (templates) => [
  ...STARTERS.map((s) => ({ key: `s-${s.key}`, label: `${s.name} (ready-made)`, ...toPayload({ ...s, shared: false }) })),
  ...templates.filter(canRun).map((t) => ({ key: `t-${t.id}`, label: t.name, name: t.name, scan_type: t.scan_type, metadata: t.metadata }))
];

// The body POSTed to /api/scan-schedules/. `row` is one address ({id, value, label}).
export function buildSchedule({ setup, row, userTargetId, frequency, day, time, timezone }) {
  if (!setup) throw new Error('Choose what to run.');
  if (!row) throw new Error('Choose an address.');
  const [run] = buildRuns(setup, userTargetId, [row]);
  return {
    user_target: userTargetId, name: `${setup.name} on ${row.value}`, scan_type: run.scan_type, options: run.options,
    frequency, day_of_week: frequency === 'weekly' ? Number(day) : 0, run_at: frequency === 'hourly' ? `00:${time.slice(3)}` : time, timezone, enabled: true
  };
}
